/* eslint-disable react-hooks/exhaustive-deps */
import DescriptionView from "components/DescriptionView";
import FinishInvestigation from "components/FinishInvestigation";
import { useAuth } from "context/AuthContext";
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { fetchFurtherInvestigationFromCRandIncidentRegNo } from "services/incidentService";
import {
  CrimeReference,
  FurtherInvestigation,
  Investigation,
  InvestigationFile,
  Roles,
} from "types";
import { DateView, hasRole } from "utils/helper";
import InvestigationForm from "./modals/InvestigationForm";
import InvestigationNarration from "./modals/InvestigationNarration";

// Reusable Action Button Component
const ActionButton: React.FC<{
  onClick: () => void;
  children: React.ReactNode;
  variant?: "primary" | "secondary" | "outline";
  className?: string;
}> = ({ onClick, children, variant = "outline", className = "" }) => {
  const baseClasses =
    "px-4 py-2 rounded-lg font-medium text-sm transition-all duration-200 flex items-center gap-2";
  const variants = {
    primary: "bg-red-600 text-white hover:bg-red-700 shadow-sm",
    secondary: "bg-gray-600 text-white hover:bg-gray-700 shadow-sm",
    outline: "border border-gray-300 text-gray-700 hover:bg-gray-50",
  };

  return (
    <button
      className={`${baseClasses} ${variants[variant]} ${className}`}
      onClick={onClick}
    >
      {children}
    </button>
  );
};

type Props = {
  incident: CrimeReference | undefined;
  file?: InvestigationFile | null;
  investigations: Investigation[] | [];
  setRefetch: React.Dispatch<React.SetStateAction<boolean>>;
  setChangeTab: React.Dispatch<React.SetStateAction<string>>;
};

const InvestigationTab: React.FC<Props> = ({
  incident,
  file,
  investigations: investigationData,
  setRefetch,
  setChangeTab,
}) => {
  const { t } = useTranslation();
  const { loginData } = useAuth();
  const [investigations, setInvestigations] = useState<Investigation[]>([]);
  const [action, setAction] = useState<"Add" | "Edit">("Add");
  const [openFinishInvestigation, setOpenInvestigation] = useState(false);
  const [furtherInvstigation, setFurtherInvestigation] =
    useState<FurtherInvestigation>();
  const [isOpenForm, setIsOpenForm] = React.useState(false);
  const [selectedFile, setSelectedFile] = useState<Investigation | undefined>();

  const fetchFurtherInvestigation = (
    cr_number: string,
    incident_registration_number: string,
  ) => {
    fetchFurtherInvestigationFromCRandIncidentRegNo(
      cr_number,
      incident_registration_number,
    ).then((response) => {
      if (response?.length > 0) {
        setFurtherInvestigation(response[0]);
      } else {
        setFurtherInvestigation(undefined);
      }
    });
  };

  useEffect(() => {
    setInvestigations(investigationData);
    if (investigationData?.length > 0) {
      const crNumber = investigationData[0]?.CR_number;
      const incidentRegNo = incident?.Incident_Registration_number;
      if (crNumber && incidentRegNo) {
        fetchFurtherInvestigation(crNumber, incidentRegNo);
      }
    }
  }, [investigationData]);

  const isProsecutor = hasRole(loginData?.roles, [Roles.prosecutor]);
  const isClerk = hasRole(loginData?.roles, [
    Roles.clerk,
    Roles.economicClerk,
    Roles.corruptionClerk,
  ]);
  const isPolice =
    hasRole(loginData?.roles, [Roles.member]) ||
    loginData?.role === "Member" ||
    loginData?.is_police;

  return (
    <div className="space-y-6">
      {/* Header Section */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center">
              <span className="text-2xl">🔍</span>
            </div>
            <div>
              <h1 className="text-2xl font-bold text-gray-900">
                {t("investigation_information")}
              </h1>
              <p className="text-gray-600 mt-1">
                {t("manage_investigation_for_incident")}
              </p>
            </div>
          </div>

          {investigations.length === 0 &&
            (isProsecutor || isClerk || isPolice) && (
              <ActionButton
                onClick={() => {
                  setAction("Add");
                  setIsOpenForm(true);
                }}
              >
                ➕ {t("add_investigation")}
              </ActionButton>
            )}
        </div>
      </div>

      {investigations.length === 0 ? (
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-8">
          <div className="text-center text-gray-500">
            {t("no_investigations_found")}
          </div>
        </div>
      ) : (
        <div className="space-y-4">
          {investigations.map((investigation, index) => (
            <div
              key={index}
              className="bg-white border border-gray-200 rounded-xl shadow-sm p-6"
            >
              <div className="space-y-6">
                {/* Action Buttons */}
                <div className="flex justify-end gap-3">
                  {!investigation?.end_date && isClerk && (
                    <button
                      type="button"
                      className="px-4 py-2 bg-red-600 text-white font-medium rounded-lg hover:bg-red-700 transition-colors"
                      title={
                        isClerk
                          ? t("submit_investigation")
                          : t("finish_investigation")
                      }
                      onClick={() => setOpenInvestigation(true)}
                    >
                      {isClerk ? t("submit") : t("finish_investigation")}
                    </button>
                  )}

                  {furtherInvstigation &&
                    furtherInvstigation?.decision === "back_to_prosecutor" && (
                      <button
                        type="button"
                        className="px-4 py-2 bg-red-600 text-white font-medium rounded-lg hover:bg-red-700 transition-colors"
                        title={t("edit_investigation")}
                        onClick={() => {
                          setSelectedFile({
                            ...investigation,
                            crime_category_id:
                              investigation?.crime_category?.uu_id,
                          });
                          setAction("Edit");
                          setIsOpenForm(true);
                        }}
                      >
                        {t("edit_investigation")}
                      </button>
                    )}
                </div>

                {/* Investigation Details */}
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                  <div>
                    <p className="text-sm font-medium text-red-600">
                      {t("cr_number")}
                    </p>
                    <p className="font-semibold text-gray-900">
                      {investigation?.CR_number}
                    </p>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-red-600">
                      {t("assigned_team")}
                    </p>
                    <p className="font-semibold text-gray-900">
                      {investigation?.assigned_team}
                    </p>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-red-600">
                      {t("start_date")}
                    </p>
                    <p className="font-semibold text-gray-900">
                      {DateView(investigation?.start_date)}
                    </p>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-red-600">
                      {t("prosecutor")}
                    </p>
                    <p className="font-semibold text-gray-900">
                      {investigation?.assigned_prosecutor}
                    </p>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-red-600">
                      {t("police")}
                    </p>
                    <p className="font-semibold text-gray-900">
                      {investigation?.assigned_police}
                    </p>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-red-600">
                      {t("status")}
                    </p>
                    <p className="font-semibold text-gray-900">
                      {investigation?.investigation_status}
                    </p>
                  </div>
                  {investigation?.end_date && (
                    <div>
                      <p className="text-sm font-medium text-red-600">
                        {t("end_date")}
                      </p>
                      <p className="font-semibold text-gray-900">
                        {DateView(investigation?.end_date)}
                      </p>
                    </div>
                  )}
                </div>

                {/* Description */}
                <DescriptionView
                  label={t("description")}
                  value={investigation?.description}
                />

                {/* Narration Section */}
                <div className="pt-6 border-t border-gray-200">
                  <InvestigationNarration
                    investigation={investigation}
                    setRefetch={setRefetch}
                    setChangeTab={setChangeTab}
                  />
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Modals */}
      {openFinishInvestigation && (
        <FinishInvestigation
          title={isClerk ? t("finish_record") : t("finish_investigation")}
          message={t("confirm_finish_investigation")}
          onCancel={() => setOpenInvestigation(false)}
          setChangeTab={setChangeTab}
          setRefetch={setRefetch}
          investigation={investigations[0]}
          file={file}
          isOpen={openFinishInvestigation}
          incident={incident}
        />
      )}

      {isOpenForm && (
        <InvestigationForm
          action={action}
          incident={incident}
          file={file}
          investigation={selectedFile}
          isOpen={isOpenForm}
          onClose={(reload) => {
            setIsOpenForm(false);
            if (reload) setRefetch((prev) => !prev);
          }}
          setRefetch={setRefetch}
        />
      )}
    </div>
  );
};

export default InvestigationTab;
